/**
 * Guarantee the canonical V2 profile shape once, where it enters the app.
 *
 * The response keeps profile data and typed demographics in separate
 * top-level containers. Every collection is optional, so skipped onboarding
 * data must be represented by safe empty collections rather than undefined.
 *
 * ## What it does and does not fill in
 *
 * **Containers only, never values.** A missing section becomes an empty
 * section; a missing list becomes `[]`. It never invents a name, photo or
 * biography: a screen rendering an empty string where the member has data
 * looks like data loss, which is worse than rendering nothing.
 *
 * **Null and undefined pass through untouched.** "Not loaded yet" has to stay
 * distinguishable from "loaded and empty", or every screen renders a blank
 * form over a pending fetch.
 *
 */

/** Role sections retained for fields that are still role-specific. */
const ROLE_SECTIONS = Object.freeze([
  "mentorProfile",
  "menteeProfile",
  "menteeMentorProfile",
  "sponsorProfile",
]);

/** Collections read directly from the canonical profile object. */
const PROFILE_LISTS = Object.freeze([
  "educations",
  "employmentHistory",
  "interests",
  "licenses",
  "photos",
  "specializations",
  "desiredSpecializations",
]);

/** Lists that may still occur inside a role-specific section. */
const SECTION_LISTS = Object.freeze([
  "expertise",
  "interests",
  "specialization",
  "specializedLicencesOrRatings",
]);

/**
 * Every demographic collection is optional (ECH-396).
 */
const DEMOGRAPHIC_LISTS = Object.freeze([
  "disabilityStatuses",
  "education",
  "ethnicities",
  "employmentInformation",
  "employmentStatus",
  "genderIdentities",
  "lgbtqIdentities",
  "languagesSpoken",
]);

const withLists = (section, keys) => {
  const out = { ...section };
  for (const key of keys) {
    if (out[key] == null) out[key] = [];
  }
  return out;
};

const normalizeCanonicalProfile = (profile) => {
  const normalized = withLists(profile, PROFILE_LISTS);

  for (const name of ROLE_SECTIONS) {
    if (Object.hasOwn(normalized, name)) {
      normalized[name] = withLists(normalized[name], SECTION_LISTS);
    }
  }

  if (normalized.dualRoleExtras != null) {
    normalized.dualRoleExtras = {
      ...normalized.dualRoleExtras,
      newCurrentSpecializations:
        normalized.dualRoleExtras.newCurrentSpecializations ?? [],
    };
  }

  return normalized;
};

export const normalizeProfile = (response) => {
  if (response == null) return response;

  const normalized = { ...response };
  normalized.profile = normalizeCanonicalProfile(response.profile ?? {});
  normalized.demographics = withLists(
    response.demographics ?? {},
    DEMOGRAPHIC_LISTS,
  );

  return normalized;
};

export default normalizeProfile;
